/**
 * useTranslation Hook
 *
 * Custom hook for accessing translations in components.
 * Provides a convenient wrapper around the language store slice.
 *
 * @example
 * ```tsx
 * const { t, currentLanguage, setLanguage, isLoading } = useTranslation();
 * return <h1>{t('home.hero.title')}</h1>;
 * ```
 *
 * @validates Requirements 4.2, 4.4
 */

import { useStore } from '../store';
import type { LocaleCode } from '../services/languageService';

/**
 * Return type for useTranslation hook
 */
export interface UseTranslationReturn {
  /**
   * Translation function - accepts a key and returns translated string.
   * Returns the key itself if translation not found (fallback).
   * @example t('home.hero.title') => "Find all words"
   */
  t: (key: string) => string;

  /**
   * Current active language code ('fr' or 'en')
   */
  currentLanguage: LocaleCode;

  /**
   * Change the current language
   * Persists to localStorage and triggers re-render of all translated content
   */
  setLanguage: (locale: LocaleCode) => Promise<void>;

  /**
   * Whether translations are being loaded/initialized
   */
  isLoading: boolean;
}

/**
 * Custom hook for accessing translations in components
 *
 * @returns Object containing:
 *   - t: Translation function
 *   - currentLanguage: Current locale code
 *   - setLanguage: Function to change language
 *   - isLoading: Whether translations are loading
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { t, currentLanguage } = useTranslation();
 *   return (
 *     <div>
 *       <p>Current language: {currentLanguage}</p>
 *       <h1>{t('home.hero.title')}</h1>
 *     </div>
 *   );
 * }
 * ```
 */
export function useTranslation(): UseTranslationReturn {
  const t = useStore((state) => state.t);
  const currentLanguage = useStore((state) => state.currentLanguage);
  const setLanguage = useStore((state) => state.setLanguage);
  const isLoading = useStore((state) => state.isLanguageLoading);

  return {
    t,
    currentLanguage,
    setLanguage,
    isLoading,
  };
}
